import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { urlApplicationRoot } from '/business/shared/util/Statics';
import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import getElectNumCertsSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectNumCertsSelector';
import getElectNumLocationsSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectNumLocationsSelector';
import getElectNumLocationsInputtedSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectNumLocationsInputtedSelector';

import useScroll from '/shared/components/functional/useScroll';
import ButtonIcon from '/shared/components/widgets/button/ButtonIcon';

import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import Colours from '/shared/styled/Colours';

import ConsumptionTable from './components/ConsumptionTable';
import TopEmissionsTable from './components/TopEmissionsTable';
import ConsumptionPie from './components/ConsumptionPie';
import ConsumptionStats from './components/ConsumptionStats';

import LocationsIcon from '/business/shared/images/icons/location-elect.svg';
import ConsumptionDataIcon from '/business/shared/images/icons/consumption-electric-data.svg';
import CertificatesIcon from '/business/shared/images/icons/cert-electric.svg';
import StyledElecBreakout from './components/__styled__/StyledElecBreakout';
import type { QuestionConfigQuestionType } from '/index';
import EducationWidget from '/business/shared/components/widgets/education/EducationWidget';
import { QUESTION_BRANCH_ACTIVITY } from '/shared/util/Statics';

const StyledElecNavigationRow = styled.div`
  margin-bottom: 1rem;
  display: flex;

  h2, h3 {
    margin: 0;
  }

  a {
    font-style: normal;
    text-decoration: none;
    color: inherit;
  }

  > :first-child {
    display: flex;
    background-color: ${Colours.whiteDark1};
    border: 1px solid ${Colours.greyLight8};
    border-right: 0;
    border-radius: 0.25rem 0 0 0.25rem;
    padding: 2rem;
    flex: 1;
    transition: 0.1s ease-in-out all;

    &:hover {
      border-color: ${Colours.green};
      background-color: ${Colours.greenLight8};
    }

    > :first-child {
      margin-right: 2rem;
      width: 3rem;

      img {
        width: 100%;
      }
    }

    > :last-child {
      flex: 1;
      padding-right: 2rem;
    }
  }

  > :last-child {
    margin-left: auto;
    text-align: center;
    padding: 2rem;
    width: 20rem;
    background-color: ${Colours.green};
    color: ${Colours.white};
    border-radius: 0 0.25rem 0.25rem 0;

    h3 {
      margin-bottom: 2rem;
    }

    strong {
      font-size: 3rem;
      line-height: 1;
      font-weight: bold;
      font-family: monospace;
    }
  }
`;

// @todo: Move into own file or reuse component
const StyledInfoList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 1rem;
  margin-top: 2rem;
  margin-bottom: 4rem;

  li {
    background: ${Colours.white};
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    border-radius: 0.25rem;
    /* justify-content: space-between; */
    > :last-child {
      padding: 1rem 0;

      > div {
        margin-bottom: 1.5rem;
      }
    }

    h3 {
      font-size: 1.1rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid ${Colours.greyLight6};
      margin-bottom: 1rem;
    }

    strong {
      font-size: 2rem;
      font-weight: bold;
      text-align: center;
    }

    table {
      width: 100%;
      border-collapse: collapse;

      tr td {
        border-bottom: 1px solid ${Colours.greyLight6};
        padding: 0.5rem 0;

        &:not(:last-child) {
          border-right: 1px solid ${Colours.whiteDark1};
        }

        &:last-child {
          text-align: right;
        }
      }
    }
  }
`;

interface ElecNavigationRowInfo {
  label: string;
  value: number | React.ReactNode;
}

interface ElecNavigationRowProps {
  to: string;
  title: string;
  body: string | React.ReactNode;
  icon?: React.ReactNode;
  info?: ElecNavigationRowInfo;
}

function ElecNavigationRow({ to, title, body, icon, info }: ElecNavigationRowProps) {
  return (
    <StyledElecNavigationRow>
      <Link to={to}>
        <div>
          <ButtonIcon displayIcon={icon} />
        </div>
        <div>
          <h2>{title}</h2>
          <p>{body}</p>
        </div>
      </Link>
      <div>
        {info && (
          <>
            <h3>{info.label}</h3>
            <div>{typeof info.value === 'number' ? <strong>{info.value}</strong> : info.value}</div>
          </>
        )}
      </div>
    </StyledElecNavigationRow>
  );
}

export default function ElectricityHeatSteamSelection({ question }: { question: QuestionConfigQuestionType }) {
  const { t } = useTranslation();
  useScroll();

  const appId = useSelector(getApplicationIdSelector);
  const { getUrlWithAppId } = useUrlBuilder();
  const numCerts = useSelector(getElectNumCertsSelector);
  const numLocations = useSelector(getElectNumLocationsSelector);
  const numLocationsInputted = useSelector(getElectNumLocationsInputtedSelector);

  const currentRoute = `/${urlApplicationRoot}/questions/calculation/scope-2/electricity-heat-steam`;

  return (
    <>
      <EducationWidget question={question} branchType={QUESTION_BRANCH_ACTIVITY} />
      <div>
        <StyledHeading as="h1" $type="h1">
          {/* @i18n: Electricity, heat and steam */}
          <Trans i18nKey="questions:electricityHeatSteam.selection.title" />
        </StyledHeading>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          {/* @i18n: To calculate emissions from your business's electricity, heat, and steam consumption[...] */}
          <Trans i18nKey="questions:electricityHeatSteam.selection.intro" />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>
            {/* @i18n: For more information, refer to the "Help" section for detailed guidance. */}
            <Trans i18nKey="questions:electricityHeatSteam.shared.helpText" />
          </div>
        </StyledInfoFlash>
      </div>
      <div>
        <ElecNavigationRow
          to={getUrlWithAppId(`${currentRoute}/locations`, appId)}
          title={t('questions:electricityHeatSteam.selection.locations.title')}
          body={
            <>
              {/* @i18n: Add all locations covered in this carbon review to enter electricity, heat, and steam[...] */}
              <Trans i18nKey="questions:electricityHeatSteam.selection.locations.body" />
            </>
          }
          icon={LocationsIcon}
          info={{
            label: t('questions:electricityHeatSteam.shared.totalLocationsAdded'),
            value: numLocations,
          }}
        />
        <ElecNavigationRow
          to={getUrlWithAppId(`${currentRoute}/consumption-data`, appId)}
          title={t('questions:electricityHeatSteam.selection.consumption.title')}
          body={
            <>
              {/* @i18n: For each location, enter consumption data provided by your utility supplier, any[...] */}
              <Trans i18nKey="questions:electricityHeatSteam.selection.consumption.body" />
            </>
          }
          icon={ConsumptionDataIcon}
          info={{
            label: t('questions:electricityHeatSteam.shared.consumptionDataAddedFor'),
            value: (
              <>
                {/* @i18n: 1 out of 3 locations */}
                <Trans
                  i18nKey="questions:electricityHeatSteam.shared.outOfLocations"
                  values={{ numLocationsInputted, numLocations }}
                  components={{ 1: <strong /> }}
                />
              </>
            ),
          }}
        />
        <ElecNavigationRow
          to={getUrlWithAppId(`${currentRoute}/energy-attribute-certificates`, appId)}
          title={t('questions:electricityHeatSteam.selection.certificates.title')}
          body={
            <>
              {/* @i18n: If your business has purchased energy attribute certificates, such as Power Purchase[...] */}
              <Trans i18nKey="questions:electricityHeatSteam.selection.certificates.body" />
            </>
          }
          icon={CertificatesIcon}
          info={{
            label: t('questions:electricityHeatSteam.shared.totalCertificatesAdded'),
            value: numCerts,
          }}
        />
      </div>
      {numLocationsInputted > 0 && (
        <StyledElecBreakout>
          <div>
            <StyledHeading as="h2" $type="h2">
              {/* @i18n: Calculated carbon emissions for electricity, heat and steam */}
              <Trans i18nKey="questions:electricityHeatSteam.shared.calculatedEmissionsForElectricityHeatSteam" />
            </StyledHeading>
            <StyledInfoList>
              <li>
                <StyledHeading as="h3" $type="h3">
                  {/* @i18n: Calculated carbon emissions* */}
                  <Trans i18nKey="questions:electricityHeatSteam.shared.calculatedCarbonEmissionsFootnote" />
                </StyledHeading>
                <div>
                  <ConsumptionStats />
                </div>
              </li>
              <li>
                <StyledHeading as="h3" $type="h3">
                  {/* @i18n: Energy attribute certificates */}
                  <Trans i18nKey="questions:electricityHeatSteam.selection.certificates.title" />
                </StyledHeading>
                <div>
                  <div>
                    <ConsumptionPie />
                  </div>
                </div>
              </li>
              <li>
                <StyledHeading as="h3" $type="h3">
                  {/* @i18n: Top 5 emission hotspots */}
                  <Trans i18nKey="questions:electricityHeatSteam.selection.topHotspots" />
                </StyledHeading>
                <div>
                  <TopEmissionsTable />
                </div>
              </li>
            </StyledInfoList>
            <ConsumptionTable />
          </div>
        </StyledElecBreakout>
      )}
    </>
  );
}
